/**
 * `doc_key` (spec §3.3): a random 128-bit id kept in `Office.context.document.settings` under
 * `ella.doc_key`. It is the ONLY thing the pane ever writes into the document, and only on the first Word
 * turn. When the file is read-only (saveAsync fails) the key is removed again and used for this pane
 * session only.
 */

export const DOC_KEY_SETTING = "ella.doc_key";
const DOC_KEY_PATTERN = /^[0-9a-f]{32}$/;

/** The slice of `Office.Settings` the pane uses. */
export interface DocSettings {
  get(name: string): unknown;
  set(name: string, value: string): void;
  remove(name: string): void;
  /** Resolves true when the settings were saved into the document. */
  save(): Promise<boolean>;
}

export interface DocKey {
  key: string;
  /** False when the key lives in pane memory only (read-only file, or no settings API). */
  persisted: boolean;
}

export function randomDocKey(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** The doc_key already saved in the document, without minting one (reading never writes). */
export function peekDocKey(settings: DocSettings | null): string | null {
  if (settings === null) return null;
  try {
    const existing = settings.get(DOC_KEY_SETTING);
    return typeof existing === "string" && DOC_KEY_PATTERN.test(existing) ? existing : null;
  } catch {
    return null;
  }
}

export async function resolveDocKey(settings: DocSettings | null, mint: () => string = randomDocKey): Promise<DocKey> {
  if (settings === null) return { key: mint(), persisted: false };
  try {
    const existing = settings.get(DOC_KEY_SETTING);
    if (typeof existing === "string" && DOC_KEY_PATTERN.test(existing)) return { key: existing, persisted: true };
  } catch {
    return { key: mint(), persisted: false };
  }
  const key = mint();
  let saved = false;
  try {
    settings.set(DOC_KEY_SETTING, key);
    saved = await settings.save();
  } catch {
    saved = false;
  }
  if (!saved) {
    try {
      settings.remove(DOC_KEY_SETTING);
    } catch {
      // Nothing was saved; the in-memory setting dies with the pane.
    }
  }
  return { key, persisted: saved };
}

/** `Office.context.document.settings`, when the host has it. */
export function officeDocSettings(): DocSettings | null {
  if (typeof Office === "undefined") return null;
  const settings = Office.context?.document?.settings;
  if (!settings) return null;
  return {
    get: (name) => settings.get(name),
    set: (name, value) => settings.set(name, value),
    remove: (name) => settings.remove(name),
    save: () =>
      new Promise<boolean>((resolve) => {
        try {
          settings.saveAsync((result) => resolve(result.status === Office.AsyncResultStatus.Succeeded));
        } catch {
          resolve(false);
        }
      }),
  };
}
